export interface MuscleMassInput {
    weightKg: number;
    bodyFatPercent: number;
    gender: 'male' | 'female';
    ageYears: number;
    heightCm: number;
}

export interface MuscleMassResult {
    fatMassKg: number;
    fatPercent: number;
    fatFreeMassKg: number;
    muscleFactor: number;
    muscleMassKg: number;
    musclePercent: number;
    otherKg: number;
    otherPercent: number;
}

const MIN_MUSCLE_FACTOR = 0.4;
const MAX_MUSCLE_FACTOR = 0.6;

// Guards against nonsense inputs reaching the estimate. The scan pipeline can
// hand us zeroed or partially populated measurements, and a silently wrong
// muscle figure is worse than showing nothing.
// 30 kg is about BMI 11 at 165 cm — no adult at a scan is lighter — and it
// is what catches "10" typed for "100". 300 kg is above anyone who could stand
// for a scan. The API refuses the same range (functions-api src/weight.ts).
export const WEIGHT_RANGE_KG = { min: 30, max: 300 };
export const BODY_FAT_RANGE_PERCENT = { min: 3, max: 70 };
const AGE_RANGE_YEARS = { min: 10, max: 120 };
const HEIGHT_RANGE_CM = { min: 100, max: 250 };

const inRange = (value: unknown, range: { min: number; max: number }): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= range.min && value <= range.max;

/**
 * Estimates skeletal muscle mass from weight, body fat, gender, age and height.
 *
 * This is an estimate for tracking consistency over time, not a medical figure.
 * Returns null when any input is missing or out of range so callers can hide
 * the metric rather than render a misleading number.
 */
export const calculateMuscleMass = (input: MuscleMassInput): MuscleMassResult | null => {
    const { weightKg, bodyFatPercent, gender, ageYears, heightCm } = input;

    if (gender !== "male" && gender !== "female") return null;
    if (!inRange(weightKg, WEIGHT_RANGE_KG)) return null;
    if (!inRange(bodyFatPercent, BODY_FAT_RANGE_PERCENT)) return null;
    if (!inRange(ageYears, AGE_RANGE_YEARS)) return null;
    if (!inRange(heightCm, HEIGHT_RANGE_CM)) return null;

    const fatMassKg = weightKg * (bodyFatPercent / 100);
    const fatFreeMassKg = weightKg - fatMassKg;

    const baseFactor = gender === "male" ? 0.52 : 0.47;
    const ageAdjustment = ageYears < 30 ? 0 : (ageYears - 30) * 0.001;
    const bodyFatReference = gender === "male" ? 15 : 25;
    const bodyFatAdjustment = (bodyFatPercent - bodyFatReference) * 0.002;
    const heightAdjustment = (heightCm - 175) * 0.0005;

    const rawFactor = baseFactor - ageAdjustment - bodyFatAdjustment + heightAdjustment;
    const muscleFactor = Math.max(MIN_MUSCLE_FACTOR, Math.min(MAX_MUSCLE_FACTOR, rawFactor));

    const muscleMassKg = fatFreeMassKg * muscleFactor;
    const musclePercent = (muscleMassKg / weightKg) * 100;

    // Everything that is neither fat nor estimated skeletal muscle: bone,
    // water, organs, non-skeletal tissue. muscleFactor is clamped below 1, so
    // this is always positive.
    const otherKg = fatFreeMassKg - muscleMassKg;

    return {
        fatMassKg,
        fatPercent: (fatMassKg / weightKg) * 100,
        fatFreeMassKg,
        muscleFactor,
        muscleMassKg,
        musclePercent,
        otherKg,
        otherPercent: (otherKg / weightKg) * 100,
    };
};

/**
 * Profiles carry either an explicit age or a birth date. Prefers age, falls
 * back to deriving whole years from birthDate.
 */
export const resolveAgeYears = (
    age?: number | null,
    birthDate?: Date | string | null
): number | null => {
    if (typeof age === "number" && Number.isFinite(age) && age > 0) return age;
    if (!birthDate) return null;

    const born = birthDate instanceof Date ? birthDate : new Date(birthDate);
    if (Number.isNaN(born.getTime())) return null;

    const now = new Date();
    let years = now.getFullYear() - born.getFullYear();
    const monthDelta = now.getMonth() - born.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < born.getDate())) {
        years -= 1;
    }
    return years > 0 ? years : null;
};
